import { Badge, Box, Button, CircularProgress, Drawer, IconButton, InputAdornment, TextField, Theme, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { DialogCloseIcon, ExportIcon, FilterIcon, InfoIconOutlined, RefreshIcon } from '../../../assets/SvgIcons';
import { SearchIcon } from '../../customSVGs';
import CustomFilter from '../../investorOverview/common/CustomFilter';
import CustomTable from '../../investorOverview/common/customTable';
import { OrderHistoryTableBody } from './tables/body';
import { orderHistoryTableHeaderData } from './tables/headers';
import { ModeOfHoldingTypeFilter, OrderHistoryRes, OrderListObj, PaymentTypeFilter, ProductTypeFilter, ResidentStatusTypeFilter, TransactionTypeFilter } from '../../../redux-store/types/orderManagement';
import { getModeOfHoldingFilter, getOrderHistoryList, getNonMfOrdersExcelReport, getPaymentTypeFilter, getProductTypeFilter, getResidentStatusFilter, getTransactionTypeFilter } from '../../../redux-store/actions/orderManagement';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '../../../utils/useDebounce';
import { RootStateType } from '../../../redux-store/reducers';
import { Order, PaginationObj, SortingObj } from '../../investorOverview/types';
import { whiteSpaceOnlyRegex } from '../../../utils/regex';
import { getAprovalStatusFilter } from '../../../redux-store/actions/orderApproval';
import { ApprovalStatuFilter } from '../../../redux-store/types/orderApproval';
import { reportDownload } from '../../Reports/report-utils';
import { showError } from '../../../redux-store/actions/auth';
import { EXPORT_TOOLTIP_TEXT } from '../../../utils/constant';

export default function OrderHistory(): JSX.Element {
    const dispatch = useDispatch();
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const [productFilterData, setProductFilterData] = useState<ProductTypeFilter[]>([])
    const [transactionFilterData, setTransactionFilterData] = useState<TransactionTypeFilter[]>([])
    const [paymentFilterData, setPaymentFilterData] = useState<PaymentTypeFilter[]>([])
    const [orderFilterData, setOrderFilterData] = useState<ApprovalStatuFilter[]>([])
    const [residentStatusFilterData, setResidentStatusFilterData] = useState<Array<ResidentStatusTypeFilter>>([])
    const [modeOfHoldingFilterData, setModeOfHoldingFilterData] = useState<Array<ModeOfHoldingTypeFilter>>([])
    const [searchText, setSearchText] = useState<string>('');
    const [orderHistoryLoading, setOrderHistoryLoading] = useState<boolean>(false);
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);
    const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
    const [orderHistoryTableData, setOrderHistoryTableData] = useState<OrderListObj[]>([])
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const theme = useTheme();
    const debounceSearchText = useDebounce(searchText, 500);

    const { iwappRmId = "" } = useSelector((store: RootStateType) => store.id)
    const isFilterApplied = productFilterData?.some(obj => obj.checked) || transactionFilterData?.some(obj => obj.checked) || paymentFilterData?.some(obj => obj.checked)


    const transactionFilterPayload = transactionFilterData?.filter(obj => obj.checked)?.map((obj: TransactionTypeFilter) => `${obj.orderStatusValue}`)

    const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map((obj: ProductTypeFilter) => `${obj.orderStatusValue}`)

    const paymentFilterPayload = paymentFilterData?.filter(obj => obj.checked === true)?.map((key: PaymentTypeFilter) => {
        if (key && key?.orderStatusId?.startsWith("paymentType-")) {
            return key?.orderStatusId?.substring("paymentType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const orderFilterPayload = orderFilterData?.filter(obj => obj.checked === true)?.map((key: ApprovalStatuFilter) => {
        if (key && key?.orderStatusId?.startsWith("orderType-")) {
            return key?.orderStatusId?.substring("orderType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const residentStatusFilterPayload = residentStatusFilterData?.filter(obj => obj.checked === true)?.map((key: ResidentStatusTypeFilter) => {
        if (key && key?.orderStatusId?.startsWith("residentStatusType-")) {
            return key?.orderStatusId?.substring("residentStatusType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const modeOfHoldingFilterPayload = modeOfHoldingFilterData?.filter(obj => obj.checked === true)?.map((key: ModeOfHoldingTypeFilter) => {
        if (key && key?.orderStatusId?.startsWith("modeOfHoldingType-")) {
            return key?.orderStatusId?.substring("modeOfHoldingType-".length); // Trim the prefix
        } else {
            return key?.orderStatusId;
        }
    })

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true);
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const fetchOrderHistoryList = async ({
        sort = "",
        order = "",
        transactionType = [],
        productType = [],
        paymentStatus = [],
        approvalStatus = [],
        investorType = [],
        modeOfHolding = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: {
        sort?: string,
        order?: string,
        transactionType?: Array<string>,
        productType?: Array<string>,
        paymentStatus?: Array<string>,
        approvalStatus?: Array<string>,
        investorType?: Array<string>,
        modeOfHolding?: Array<string>,
        searchInput?: string,
        currentPage?: number,
        rowsPerPage?: number
    }) => {
        try {
            setOrderHistoryLoading(true);

            const payload = {
                rmId: `${iwappRmId}`,
                transactionType,
                productType,
                paymentStatus,
                approvalStatus,
                investorType,
                modeOfHolding,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage
            }

            const orderHistoryListRes = await (dispatch(getOrderHistoryList(payload)) as unknown as Promise<OrderHistoryRes>)

            const { orders = [], totalCount = 0 } = orderHistoryListRes;

            setOrderHistoryTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error', error);
        } finally {
            setOrderHistoryLoading(false);
        }
    }

    const handleApplyFilters = async () => {
        setFilterDrawerOpen(false)

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        const totalAppliedFilters =
            (transactionFilterPayload?.length ?? 0) +
            (productFilterPayload?.length ?? 0) +
            (paymentFilterPayload?.length ?? 0) +
            (orderFilterPayload?.length ?? 0) +
            (residentStatusFilterPayload?.length ?? 0) +
            (modeOfHoldingFilterPayload?.length ?? 0);

        setTotalFilters(totalAppliedFilters)

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

    }

    const handleFiltersClearAll = async () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setTransactionFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setPaymentFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setOrderFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setResidentStatusFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setModeOfHoldingFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        setTotalFilters(0);

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

        setFilterDrawerOpen(false)
    }

    const handleProductTypeFilterChange = (value: string, checked: boolean) => {
        setProductFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }
    const handleTransactionTypeFilterChange = (value: string, checked: boolean) => {
        setTransactionFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }
    const handlePaymentTypeFilterChange = (value: string, checked: boolean) => {
        setPaymentFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleOrderTypeFilterChange = (value: string, checked: boolean) => {
        setOrderFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleResidentStatusTypeFilterChange = (value: string, checked: boolean) => {
        setResidentStatusFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleModeOfHoldingTypeFilterChange = (value: string, checked: boolean) => {
        setModeOfHoldingFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    const fetchProductFilterData = async () => {
        const productFilterRes = await (dispatch(getProductTypeFilter()) as unknown as Promise<ProductTypeFilter[]>)

        setProductFilterData(productFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `productType-${obj.orderStatusId}`
        })));
    }

    const fetchTransactionFilterData = async () => {
        const transactionFilterRes = await (dispatch(getTransactionTypeFilter()) as unknown as Promise<TransactionTypeFilter[]>)
        setTransactionFilterData(transactionFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `transactionType-${obj.orderStatusId}`
        })));
    }

    const fetchPaymentFilterData = async () => {
        const paymentFilterRes = await (dispatch(getPaymentTypeFilter()) as unknown as Promise<PaymentTypeFilter[]>)
        setPaymentFilterData(paymentFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `paymentType-${obj.orderStatusId}`
        })))
    }

    const fetchOrderFilterData = async () => {
        const orderFilterRes = await (dispatch(getAprovalStatusFilter()) as unknown as Promise<ApprovalStatuFilter[]>)
        setOrderFilterData(orderFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `orderType-${obj.orderStatusId}`
        })))
    }

    const fetchResidentStatusFilterData = async () => {
        const residentStatusFilterRes = await (dispatch(getResidentStatusFilter()) as unknown as Promise<Array<ResidentStatusTypeFilter>>)
        setResidentStatusFilterData(residentStatusFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `residentStatusType-${obj.orderStatusId}`
        })))
    }

    const fetchModeOfHoldingFilterData = async () => {
        const modeOfHoldingFilterRes = await (dispatch(getModeOfHoldingFilter()) as unknown as Promise<Array<ModeOfHoldingTypeFilter>>)
        setModeOfHoldingFilterData(modeOfHoldingFilterRes?.map(obj => ({
            ...obj,
            orderStatusId: `modeOfHoldingType-${obj.orderStatusId}`
        })))
    }

    const fetchFilterData = async () => {
        try {
            setIsFiltersLoading(true);

            await Promise.allSettled([
                fetchProductFilterData(),
                fetchTransactionFilterData(),
                fetchPaymentFilterData(),
                fetchOrderFilterData(),
                fetchResidentStatusFilterData(),
                fetchModeOfHoldingFilterData()
            ])
        } catch (e) {
            console.log('erorr', e);
        }
        finally {
            setIsFiltersLoading(false);
        }
    }

    const handleSort = async (orderBy: string, order: Order) => {

        setTableSortingObj({
            order,
            orderBy
        })

        fetchOrderHistoryList({
            sort: orderBy,
            order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: tablePaginationObj?.currentPage + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }


    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            rowsPerPage: Number(event.target.value),
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleExport = async () => {
        try {
            setIsExportLoading(true);

            const payload = {
                rmId: `${iwappRmId}`,
                transactionType: transactionFilterPayload,
                productType: productFilterPayload,
                paymentStatus: paymentFilterPayload,
                approvalStatus: orderFilterPayload,
                investorType: residentStatusFilterPayload,
                modeOfHolding: modeOfHoldingFilterPayload,
                searchInput: searchText.trim(), // NOTE: intentionally uses searchText (not debounceSearchText) — export is a point-in-time action that should reflect the user's exact current input.
                sort: tableSortingObj?.orderBy,
                order: tableSortingObj?.order || "desc",
                currentPage: 1,
                rowsPerPage: tablePaginationObj?.rowsPerPage, // NOTE: backend ignores pagination for export and returns the full filtered result set.
            };

            const blob = await (dispatch(getNonMfOrdersExcelReport(payload)) as unknown as Promise<Blob>);
            reportDownload(blob, 'OrderHistory.xlsx');
        } catch (error) {
            console.error('export error', error);
            dispatch(showError('Failed to export. Please try again.'));
        } finally {
            setIsExportLoading(false);
        }
    }

    const handleRefreshIconClick = async () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setTransactionFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setPaymentFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setOrderFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setResidentStatusFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setModeOfHoldingFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0,
            rowsPerPage: 10,
            totalRows: 0
        }))

        setTotalFilters(0);

        setSearchText("");

        setTableSortingObj({
            order: "desc",
            orderBy: ""
        })

        fetchOrderHistoryList({
            sort: "",
            order: "desc",
            transactionType: [],
            productType: [],
            paymentStatus: [],
            approvalStatus: [],
            searchInput: "",
            currentPage: 1,
            rowsPerPage: 10,
        })
    }

    useEffect(() => {
        fetchFilterData();
    }, [])

    useEffect(() => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        fetchOrderHistoryList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            transactionType: transactionFilterPayload,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            approvalStatus: orderFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }, [debounceSearchText])

    return (
        <>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: "15.5px",
                    borderRadius: "25px",
                    padding: "20px 30px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Order History
                    </Typography>

                    <Box sx={{ display: 'flex', columnGap: '10px', alignItems: 'center' }}>

                        <IconButton
                            sx={{
                                cursor: 'pointer',
                            }}
                            onClick={handleRefreshIconClick}>
                            <RefreshIcon />
                        </IconButton>

                        {/* Filters */}
                        <Button
                            variant="text"
                            startIcon={
                                <Badge
                                    showZero
                                    badgeContent={totalFilters}
                                    sx={(theme: Theme) => ({
                                        '& .MuiBadge-badge': {
                                            right: 0,
                                            top: -3,
                                            backgroundColor: theme.palette.primary[500],
                                            color: theme.palette.common.white,
                                            ...theme.typography.subtitle_600
                                        },
                                    })}
                                >
                                    <FilterIcon />
                                </Badge>
                            }
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                padding: "5px",
                                ...theme.typography.body_500,
                                // "&:hover": {
                                //     backgroundColor: 'unset',
                                //     color: theme.palette.text.secondary
                                // }
                            })}
                            TouchRippleProps={{
                                style: {
                                    color: theme.palette.grey[300]
                                }
                            }}
                            onClick={handleOpenFilterDrawer}
                        >
                            Filters
                        </Button>


                        {/* Export */}
                        {/* NOTE (BP-12): Box component={'button'} is intentional here — consistent with surrounding export controls in this file. Accessibility (focus, disabled state) is handled via sx props. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Box
                                component={'button'}
                                onClick={handleExport}
                                disabled={isExportLoading}
                                sx={{ cursor: isExportLoading ? 'not-allowed' : 'pointer', border: 'none', display: 'flex', gap: '10px', background: 'transparent', alignItems: 'center', opacity: isExportLoading ? 0.6 : 1 }}
                            >
                                {isExportLoading
                                    ? <CircularProgress size={18} sx={{ color: theme.palette.text.primary }} />
                                    : <ExportIcon sx={{ color: theme.palette.text.primary }} />
                                }
                                <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>Export</Typography>
                            </Box>
                            <Tooltip
                                title={EXPORT_TOOLTIP_TEXT}
                                placement="top"
                                arrow
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            ...theme.typography.subtitle_600,
                                            color: theme.palette.text.primary,
                                            backgroundColor: theme.palette.background.default,
                                            boxShadow: theme.shadows[2]
                                        }
                                    },
                                    arrow: {
                                        sx: {
                                            color: theme.palette.background.default
                                        }
                                    }
                                }}
                            >
                                <IconButton size="small" sx={{ padding: '2px' }} aria-label="Export info">
                                    <InfoIconOutlined />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Search Input */}
                        <TextField
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                            onMouseDown={handleClearSearch}
                                            aria-label="Clear Search"
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: (theme: Theme) => ({
                                    borderRadius: '57px',
                                    height: '47.17px',
                                    border: `1px solid ${theme.palette.text.secondary}B2`,
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '& input': {
                                        paddingLeft: '10px',
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            ...theme.typography.subtitle_400,
                                            color: theme.palette.text.secondary,
                                            opacity: 1,
                                        },
                                    },
                                }),
                            }}
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearchTextChange}
                        />
                    </Box>
                </Box>

                {/* Order History Table */}
                <Box sx={{ mt: '35px' }}>
                    <CustomTable
                        isLoading={orderHistoryLoading}
                        tableBody={
                            <OrderHistoryTableBody data={orderHistoryTableData ?? []} comingFrom='orderHistory' />
                        }
                        tableData={orderHistoryTableData ?? []}
                        tableHeaderData={orderHistoryTableHeaderData}
                        isPaginationVisible={true}
                        tableDataCount={tablePaginationObj?.totalRows ?? 0}
                        handleSort={handleSort}
                        order={tableSortingObj.order}
                        orderBy={tableSortingObj.orderBy}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        currentPage={tablePaginationObj.currentPage}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th': {
                                // backgroundColor: '#EDF2F759',
                            },
                            '& th:first-of-type': {
                                paddingLeft: "0px",
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={5}
                    />
                </Box>
            </Box>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    showAmcFilter={false}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    showProductTypeFilter={true}
                    showPaymentTypeFilter={true}
                    showTransactionTypeFilter={true}
                    showApprovalTypeFilter={true}
                    showResidentStatusTypeFilter={true}
                    showModeOfHoldingTypeFilter={true}
                    productTypeFilterData={productFilterData}
                    transactionFilterData={transactionFilterData}
                    paymentFilterData={paymentFilterData}
                    approvalTypeFilterData={orderFilterData}
                    residentStatusFilterData={residentStatusFilterData}
                    modeOfHoldingFilterData={modeOfHoldingFilterData}
                    handleProductTypeFilterChange={handleProductTypeFilterChange}
                    handlePaymentTypeFilterChange={handlePaymentTypeFilterChange}
                    handleTransactionTypeFilterChange={handleTransactionTypeFilterChange}
                    handleApprovalTypeFilterChange={handleOrderTypeFilterChange}
                    handleResidentStatusTypeFilterChange={handleResidentStatusTypeFilterChange}
                    handleModeOfHoldingTypeFilterChange={handleModeOfHoldingTypeFilterChange}
                    isFilterApplied={isFilterApplied}
                    defaultExpandedFilter="productType"
                />
            </Drawer>
        </>

    )
}
